import { BusinessBusiness } from "../types";
import { AudienceModeId } from "../config/audienceModes";
import { calculateAudienceScore } from "./audienceScoring";
import { getUpgradedOrganicScore } from "./discoveryScoring";
import { getDistanceInMiles } from "./distance";
import { getLatestRelevantSignalDateISO, getSignalUpdatedTimestamp } from "./signalNormalization";
import {
  BUSINESS_EXPLORER_POPULARITY_RANK,
  getPopularityTierRank,
  getEffectivePopularityScore,
  calculateBusinessPopularity
} from "./popularity";

export interface RankedBusinessSnapshot {
  business: BusinessBusiness;
  rank: number;
  searchSessionId: string;
  audienceMode: AudienceModeId;
  sortBy: string;
  audienceScore: number;
  confidenceRank: number;        // audienceScore.levelRank (5 = High, 4 = Possible, 3 = Unconfirmed, 2 = Improbable, 1 = Low)
  confidenceLevel: string;       // level string e.g. "High", "Possible"
  freshnessTimestamp: number;    // timestamp ms of newest relevant signal, or -1 if Unknown
  freshnessValue: number;        // confidence multiplier 0.0 to 1.0
  distanceMiles: number;         // distance in miles or 999999 if null
  rating: number;                // business.rating || 0
  eligibilityRank: number;       // 1 = eligible, 0 = disqualified
  id: string;                    // business.id
  rankingReferenceTime: number;  // fixed reference time when search finished
  upgradedScore: number;
}

export const SCORE_TIE_EPSILON = 0.5;
export const CONFIDENCE_TIE_EPSILON = 0.02;

export function compareScoreWithTolerance(
  a: number | null | undefined,
  b: number | null | undefined,
  epsilon = SCORE_TIE_EPSILON
): number {
  const aMissing = a === null || a === undefined || isNaN(a);
  const bMissing = b === null || b === undefined || isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (Math.abs(a - b) <= epsilon) {
    return 0;
  }

  return b - a;
}

export function compareNullableDescending(
  a: number | null | undefined,
  b: number | null | undefined
): number {
  const aMissing = a === null || a === undefined || isNaN(a);
  const bMissing = b === null || b === undefined || isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1; // missing comes AFTER verified
  if (bMissing) return -1;

  return b - a;
}

export function compareNullableAscending(
  a: number | null | undefined,
  b: number | null | undefined
): number {
  const aMissing = a === null || a === undefined || isNaN(a) || a === 999999;
  const bMissing = b === null || b === undefined || isNaN(b) || b === 999999;

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  return a - b;
}

export function compareDatesDescending(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number {
  const aMs = a ? new Date(a).getTime() : -1;
  const bMs = b ? new Date(b).getTime() : -1;
  const aValid = !isNaN(aMs) && aMs > 0;
  const bValid = !isNaN(bMs) && bMs > 0;

  if (!aValid && !bValid) return 0;
  if (!aValid) return 1;
  if (!bValid) return -1;

  return bMs - aMs;
}

export function getBayesianRating(rating: number | null | undefined, reviewCount: number | null | undefined): number {
  const r = rating ?? 0;
  const v = reviewCount ?? 0;
  if (r <= 0) return 0;
  return (r * v + 3.5 * 5) / (v + 5);
}

export function compareBayesianAdjustedRatingDescending(a: any, b: any): number {
  const aRev = a.reviewCount ?? a.user_ratings_total ?? a.reviews_count;
  const bRev = b.reviewCount ?? b.user_ratings_total ?? b.reviews_count;
  const aBayes = getBayesianRating(a.rating, aRev);
  const bBayes = getBayesianRating(b.rating, bRev);
  return bBayes - aBayes;
}

export function getStableBusinessId(business: any): string {
  if (!business) return "";
  return String(business.id || business.place_id || business.name || "");
}

function compareUnknownPopularity(
  businessA: BusinessBusiness,
  businessB: BusinessBusiness,
  a: any,
  b: any
): number {
  // 1. Data completeness
  const aCompleteness = (businessA.website ? 1 : 0) + (businessA.phone ? 1 : 0) + (businessA.address ? 1 : 0) + (businessA.hours ? 1 : 0) + (businessA.category ? 1 : 0);
  const bCompleteness = (businessB.website ? 1 : 0) + (businessB.phone ? 1 : 0) + (businessB.address ? 1 : 0) + (businessB.hours ? 1 : 0) + (businessB.category ? 1 : 0);
  if (aCompleteness !== bCompleteness) {
    return bCompleteness - aCompleteness;
  }

  // 2. Identity confidence
  const aIdConf = ((businessA as any).place_id ? 2 : 0) + ((businessA as any).verificationStatus === "verified" ? 1 : 0);
  const bIdConf = ((businessB as any).place_id ? 2 : 0) + ((businessB as any).verificationStatus === "verified" ? 1 : 0);
  if (aIdConf !== bIdConf) {
    return bIdConf - aIdConf;
  }

  // 3. Local consumer evidence
  const aEvid = (businessA.rating ? 1 : 0) + ((businessA as any).photos && (businessA as any).photos.length > 0 ? 1 : 0);
  const bEvid = (businessB.rating ? 1 : 0) + ((businessB as any).photos && (businessB as any).photos.length > 0 ? 1 : 0);
  if (aEvid !== bEvid) {
    return bEvid - aEvid;
  }

  // 4. Review volume
  const aRev = (businessA as any).reviewCount ?? businessA.reviewsCount ?? (businessA as any).user_ratings_total ?? (businessA as any).reviews_count;
  const bRev = (businessB as any).reviewCount ?? businessB.reviewsCount ?? (businessB as any).user_ratings_total ?? (businessB as any).reviews_count;
  const revDiff = compareNullableDescending(aRev, bRev);
  if (revDiff !== 0) return revDiff;

  // 5. Rating confidence
  const bayesDiff = compareBayesianAdjustedRatingDescending(businessA, businessB);
  if (bayesDiff !== 0) return bayesDiff;

  // 6. Distance
  const aDist = "distanceMiles" in a ? a.distanceMiles : ((businessA as any).distanceMiles ?? null);
  const bDist = "distanceMiles" in b ? b.distanceMiles : ((businessB as any).distanceMiles ?? null);
  const distDiff = compareNullableAscending(aDist, bDist);
  if (distDiff !== 0) return distDiff;

  // 7. Stable ID
  return getStableBusinessId(businessA).localeCompare(getStableBusinessId(businessB));
}

export function compareBusinessExplorerBestMatch(
  a: BusinessBusiness | RankedBusinessSnapshot,
  b: BusinessBusiness | RankedBusinessSnapshot
): number {
  const businessA: BusinessBusiness = "business" in a ? a.business : a;
  const businessB: BusinessBusiness = "business" in b ? b.business : b;

  // 0. Disqualification / Eligibility (closed, invalid, blocked)
  const aEligible = "eligibilityRank" in a ? a.eligibilityRank : ((businessA as any).isClosed || (businessA as any).isBlocked ? 0 : 1);
  const bEligible = "eligibilityRank" in b ? b.eligibilityRank : ((businessB as any).isClosed || (businessB as any).isBlocked ? 0 : 1);
  if (aEligible !== bEligible) {
    return bEligible - aEligible;
  }

  const aPopularity = (businessA as any).popularityResult || (a as any).popularityResult || calculateBusinessPopularity(businessA);
  const bPopularity = (businessB as any).popularityResult || (b as any).popularityResult || calculateBusinessPopularity(businessB);

  const aTier = getPopularityTierRank(aPopularity?.explorerLevel || aPopularity?.level || aPopularity?.popularity_level);
  const bTier = getPopularityTierRank(bPopularity?.explorerLevel || bPopularity?.level || bPopularity?.popularity_level);

  // 1. Higher popularity tier first.
  if (aTier !== bTier) {
    return bTier - aTier;
  }

  // If both are unknown (tier 0), order by Unknown group tie-breakers:
  if (aTier === 0 && bTier === 0) {
    return compareUnknownPopularity(businessA, businessB, a, b);
  }

  // 2. Higher effective popularity score first.
  const aEffectivePopularity = getEffectivePopularityScore(aPopularity);
  const bEffectivePopularity = getEffectivePopularityScore(bPopularity);

  const effScoreDiff = compareScoreWithTolerance(aEffectivePopularity, bEffectivePopularity, SCORE_TIE_EPSILON);
  if (effScoreDiff !== 0) {
    return effScoreDiff;
  }

  // 3. Higher confidence first.
  const aConf = aPopularity?.confidence ?? aPopularity?.popularity_confidence ?? 0;
  const bConf = bPopularity?.confidence ?? bPopularity?.popularity_confidence ?? 0;
  const confDiff = compareScoreWithTolerance(aConf, bConf, CONFIDENCE_TIE_EPSILON);
  if (confDiff !== 0) {
    return confDiff;
  }

  // 4. Stronger exact-location traction first.
  const aLocTraction = aPopularity?.locationPopularityScore ?? aPopularity?.location_popularity_score;
  const bLocTraction = bPopularity?.locationPopularityScore ?? bPopularity?.location_popularity_score;
  const locationTractionDiff = compareNullableDescending(aLocTraction, bLocTraction);
  if (locationTractionDiff !== 0) {
    return locationTractionDiff;
  }

  // 5. Stronger brand reach first.
  const aBrandReach = aPopularity?.brandPopularityScore ?? aPopularity?.brand_popularity_score;
  const bBrandReach = bPopularity?.brandPopularityScore ?? bPopularity?.brand_popularity_score;
  const brandDiff = compareNullableDescending(aBrandReach, bBrandReach);
  if (brandDiff !== 0) {
    return brandDiff;
  }

  // 6. Faster verified growth first.
  const aGrowth = aPopularity?.growthMomentumScore ?? aPopularity?.growth_momentum_score;
  const bGrowth = bPopularity?.growthMomentumScore ?? bPopularity?.growth_momentum_score;
  const growthDiff = compareNullableDescending(aGrowth, bGrowth);
  if (growthDiff !== 0) {
    return growthDiff;
  }

  // 7. Stronger local market prominence first.
  const aLocalProm = aPopularity?.localProminenceScore ?? aPopularity?.local_prominence_score;
  const bLocalProm = bPopularity?.localProminenceScore ?? bPopularity?.local_prominence_score;
  const localPromDiff = compareNullableDescending(aLocalProm, bLocalProm);
  if (localPromDiff !== 0) {
    return localPromDiff;
  }

  // 8. More recent meaningful evidence first.
  const aDate = aPopularity?.displayedLastUpdatedAt ?? aPopularity?.popularity_last_checked_at ?? ("freshnessTimestamp" in a ? a.freshnessTimestamp : null);
  const bDate = bPopularity?.displayedLastUpdatedAt ?? bPopularity?.popularity_last_checked_at ?? ("freshnessTimestamp" in b ? b.freshnessTimestamp : null);
  const freshnessDiff = compareDatesDescending(aDate, bDate);
  if (freshnessDiff !== 0) {
    return freshnessDiff;
  }

  // 9. Stronger recent review activity first.
  const aReviewVel = aPopularity?.reviewVelocityScore ?? aPopularity?.review_velocity_score;
  const bReviewVel = bPopularity?.reviewVelocityScore ?? bPopularity?.review_velocity_score;
  const reviewVelDiff = compareNullableDescending(aReviewVel, bReviewVel);
  if (reviewVelDiff !== 0) {
    return reviewVelDiff;
  }

  // 10. Higher review volume first.
  const aRevCount = (businessA as any).reviewCount ?? businessA.reviewsCount ?? (businessA as any).user_ratings_total ?? (businessA as any).reviews_count;
  const bRevCount = (businessB as any).reviewCount ?? businessB.reviewsCount ?? (businessB as any).user_ratings_total ?? (businessB as any).reviews_count;
  const reviewCountDiff = compareNullableDescending(aRevCount, bRevCount);
  if (reviewCountDiff !== 0) {
    return reviewCountDiff;
  }

  // 11. Higher rating only after adequate review volume.
  const ratingDiff = compareBayesianAdjustedRatingDescending(businessA, businessB);
  if (ratingDiff !== 0) {
    return ratingDiff;
  }

  // 12. Shorter distance as a late tie-breaker.
  const aDist = "distanceMiles" in a ? a.distanceMiles : ((businessA as any).distanceMiles ?? null);
  const bDist = "distanceMiles" in b ? b.distanceMiles : ((businessB as any).distanceMiles ?? null);
  const distanceDiff = compareNullableAscending(aDist, bDist);
  if (distanceDiff !== 0) {
    return distanceDiff;
  }

  // 13. Deterministic final tie-breaker.
  return getStableBusinessId(businessA).localeCompare(getStableBusinessId(businessB));
}

/**
 * Deterministic Best Match Comparator
 * Always returns the exact same result for identical inputs.
 * Uses stable internal numerical values (not visible text labels).
 */
export function compareBestMatch(
  a: RankedBusinessSnapshot,
  b: RankedBusinessSnapshot,
  sortBy: string = "best_match"
): number {
  if (a.audienceMode === "business_explorer" && sortBy === "best_match") {
    return compareBusinessExplorerBestMatch(a, b);
  }

  if (sortBy === "last_updated") {
    // 1. Critical eligibility / disqualification state (always compared first)
    if (a.eligibilityRank !== b.eligibilityRank) {
      return b.eligibilityRank - a.eligibilityRank;
    }
    // 2. Newest relevant signal date first. Unknown (-1) appears last.
    if (a.freshnessTimestamp !== b.freshnessTimestamp) {
      if (a.freshnessTimestamp === -1) return 1;
      if (b.freshnessTimestamp === -1) return -1;
      return b.freshnessTimestamp - a.freshnessTimestamp;
    }
    // 3. Tie-breaker: Business ID ascending
    return String(a.id).localeCompare(String(b.id));
  }

  if (sortBy === "rating") {
    const scoreA = a.rating * 150 + a.upgradedScore;
    const scoreB = b.rating * 150 + b.upgradedScore;
    if (scoreB !== scoreA) return scoreB - scoreA;
  }

  if (sortBy === "distance") {
    if (a.distanceMiles !== b.distanceMiles) {
      return a.distanceMiles - b.distanceMiles;
    }
  }

  // Best Match / Opportunity Score / Default
  // 1. Critical eligibility / disqualification state (always compared first)
  if (a.eligibilityRank !== b.eligibilityRank) {
    return b.eligibilityRank - a.eligibilityRank;
  }

  // 2. Audience score descending
  if (b.audienceScore !== a.audienceScore) {
    return b.audienceScore - a.audienceScore;
  }

  // 3. Confidence level / Audience Level Rank descending (5 = High, 4 = Possible, 3 = Unconfirmed, 2 = Improbable, 1 = Low)
  if (b.confidenceRank !== a.confidenceRank) {
    return b.confidenceRank - a.confidenceRank;
  }

  // 4. Freshness timestamp descending (fresher relevant signal date first, Unknown comes last)
  if (b.freshnessTimestamp !== a.freshnessTimestamp) {
    if (a.freshnessTimestamp === -1) return 1;
    if (b.freshnessTimestamp === -1) return -1;
    return b.freshnessTimestamp - a.freshnessTimestamp;
  }

  // 5. Distance ascending when relevant
  if (a.distanceMiles !== b.distanceMiles) {
    return a.distanceMiles - b.distanceMiles;
  }

  // 6. Stable business ID ascending (deterministic string comparison fallback)
  return String(a.id).localeCompare(String(b.id));
}

/**
 * Ranks search results ONCE and locks the ordered snapshot list.
 */
export function rankSearchResultsOnce(
  businesses: BusinessBusiness[],
  audienceMode: AudienceModeId,
  sortBy: string = "best_match",
  searchCenter: { lat: number; lng: number } | null,
  userCoords: { lat: number; lng: number } | null,
  keyword: string,
  searchSessionId: string = "session_default",
  rankingReferenceTime: number = Date.now()
): {
  rankedSnapshots: RankedBusinessSnapshot[];
  sortedBusinesses: BusinessBusiness[];
  referenceTime: number;
} {
  const referenceNowISO = new Date(rankingReferenceTime).toISOString();
  const targetCenter = searchCenter || userCoords;

  // 1. Create snapshots with fixed reference time
  const rawSnapshots: RankedBusinessSnapshot[] = businesses.map((business) => {
    let distanceMiles = 999999;
    if (targetCenter && business.location?.lat && business.location?.lng) {
      const d = getDistanceInMiles(targetCenter.lat, targetCenter.lng, business.location.lat, business.location.lng);
      if (!isNaN(d)) distanceMiles = d;
    }

    const audienceResult = calculateAudienceScore(business, distanceMiles, keyword, audienceMode, undefined, referenceNowISO);
    const upgradedResult = getUpgradedOrganicScore(business, distanceMiles, keyword, undefined, undefined, audienceMode);

    // Disqualification check
    const warnings = upgradedResult?.prospect?.categorizedSignals?.warnings || [];
    const statusLower = (business.hiringStatus || "").toLowerCase();
    const isClosed = warnings.includes("Possibly Closed") || statusLower === "closed" || business.isBlocked;
    const isInvalid = warnings.includes("Nonexistent Business") || warnings.includes("Invalid Business Identity") || statusLower === "nonexistent";
    const isFraudulentOrUnsafe = warnings.includes("Fraudulent Listing") || warnings.includes("Unsafe Listing");
    const isConfirmedNotHiring = statusLower === "not_hiring" || statusLower === "confirmed_not_hiring";
    const isBlocked = !!business.isBlocked;
    const isIrrelevant = Boolean(keyword && keyword.trim().length > 0 && (upgradedResult.totalScore === 0 || upgradedResult.totalSmartScore === 0));

    const eligibilityRank = (isClosed || isInvalid || isFraudulentOrUnsafe || isConfirmedNotHiring || isBlocked || isIrrelevant) ? 0 : 1;

    const latestRelevantIso = getLatestRelevantSignalDateISO(business, audienceMode, distanceMiles === 999999 ? null : distanceMiles, keyword);
    const tsMs = latestRelevantIso ? new Date(latestRelevantIso).getTime() : -1;
    const freshnessTimestamp = isNaN(tsMs) ? -1 : tsMs;

    return {
      business,
      rank: 0, // Assigned after sort
      searchSessionId,
      audienceMode,
      sortBy,
      audienceScore: audienceResult.score,
      confidenceRank: audienceResult.levelRank,
      confidenceLevel: audienceResult.level,
      freshnessTimestamp,
      freshnessValue: audienceResult.confidence,
      distanceMiles,
      rating: business.rating || 0,
      eligibilityRank,
      id: business.id,
      rankingReferenceTime,
      upgradedScore: upgradedResult.totalScore || 0,
    };
  });

  // 2. Sort immutable copy using deterministic comparator
  const sortedSnapshots = [...rawSnapshots].sort((a, b) => compareBestMatch(a, b, sortBy));

  // 3. Assign 1-indexed ranks
  sortedSnapshots.forEach((snap, idx) => {
    snap.rank = idx + 1;
  });

  const sortedBusinesses = sortedSnapshots.map((snap) => snap.business);

  return {
    rankedSnapshots: sortedSnapshots,
    sortedBusinesses,
    referenceTime: rankingReferenceTime,
  };
}

/**
 * Development-only stability warning validator
 */
export function verifyOrderStabilityDev(
  previousOrder: string[],
  newOrder: string[],
  trigger: string,
  searchSessionId: string,
  previousScores?: Record<string, number>,
  newScores?: Record<string, number>,
  previousTimestamps?: Record<string, number>,
  newTimestamps?: Record<string, number>
) {
  if (typeof window === "undefined" || (typeof process !== "undefined" && process.env.NODE_ENV === "production")) return;

  const orderChanged = previousOrder.length !== newOrder.length ||
    previousOrder.some((id, idx) => id !== newOrder[idx]);

  if (orderChanged) {
    console.warn(
      `[BizNearYou Ranking Guard] WARNING: Result order changed! Trigger: "${trigger}". Search Session ID: ${searchSessionId}`,
      {
        previousOrder,
        newOrder,
        trigger,
        searchSessionId,
        previousScores,
        newScores,
        previousTimestamps,
        newTimestamps,
      }
    );
  }
}
